import React, { useMemo } from "react";
import { BarChart3 } from "lucide-react";
import { ProcessedScanResult } from "../SpaceScanner.types";
import { CollapsibleSection } from "./CollapsibleSection";
import {
  analyzeCapacityIssues,
  CapacityInsight,
  CapacitySeverity
} from "../../../../../services/ai/SpaceOptimiserAI";
import { BookableArea } from "../../../../../types/venues";

interface CapacityInsightsProps {
  results: ProcessedScanResult[];
  venueConfig?: { bookable_areas?: BookableArea[] };
}

const SEVERITY_COLOURS: Record<CapacitySeverity, string> = {
  info: "hsl(var(--info))",
  warning: "hsl(var(--warning))",
  critical: "hsl(var(--destructive))"
};

const CapacityInsightRow = ({
  insight
}: {
  insight: CapacityInsight;
}): React.ReactElement => {
  const colour = SEVERITY_COLOURS[insight.severity];

  return (
    <div className="flex items-start gap-2.5 rounded-md bg-card p-2.5">
      <span
        className="mt-0.5 flex size-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
        style={{ backgroundColor: colour }}
        aria-label={`${insight.capacityPercentage}% capacity`}
      >
        {insight.capacityPercentage}%
      </span>
      <div className="min-w-0 flex-1">
        <span className="text-xs font-semibold" style={{ color: colour }}>
          {insight.timePeriod}
        </span>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {insight.suggestion}
        </p>
      </div>
    </div>
  );
};

export const CapacityInsights = ({
  results,
  venueConfig
}: CapacityInsightsProps): React.ReactElement | null => {
  const insights = useMemo(
    () => analyzeCapacityIssues(results, venueConfig),
    [results, venueConfig]
  );

  if (insights.length === 0) {
    return null;
  }

  return (
    <CollapsibleSection
      icon={
        <BarChart3
          className="size-4 flex-shrink-0 text-foreground"
          aria-hidden="true"
        />
      }
      title={`Capacity Analysis (${insights.length})`}
      ariaLabel="Capacity insights"
    >
      {insights.map((insight, index) => (
        <CapacityInsightRow
          key={`${insight.timePeriod}-${insight.severity}-${index}`}
          insight={insight}
        />
      ))}
    </CollapsibleSection>
  );
};
